// client/src/components/pages/Geofencing/vehicleStatus.tsx (converted from .js, ST-00244)
// Shared visual language for LexiStar fleet vehicles on Live View — status + asset-class atoms
// used by the vehicle rail, the detail panel and the map popup so they stay in lock-step.
//
// ST-00253 / ST-00244 (2026-10-19): a vehicle's colour comes from the ONE asset status scheme
// (map/assetStatus). A vehicle with a LexiStar status takes the status board's key (the control
// room's placement, so it matches the Status Board and Replay); one without falls back to its
// motion (moving = unallocated, standing = parked, no recent report = offline). Motion ids match
// the /fleet endpoint: moving | idling | parked | offline. Class ids match
// telemetry_devices.vehicle_class: truck | loader.
//
// ST-00276 phase 2 (map colours & states): with the vehicle's `display` (the organisation's display
// rules — the status board row's, or for a vehicle without a row the rules over its motion) the
// colour and words come from the display, and its overlay marks show beside the pill.
import type { ComponentType } from 'react';
import DisplayMarks from '../../map/DisplayMarks';
import type { AssetDisplay } from '../../map/displayRules';
import { FaTruck, FaTractor } from 'react-icons/fa';
import {
  ASSET_STATUS,
  ASSET_STATUS_CLASSES,
  statusKeyOfMotion,
  type AssetStatusKey,
} from '../../map/assetStatus';
import type { FleetStatus } from '../../../types/clientPages/geofencing';

/** Motion labels (the /fleet status, and Live View's own speed + age status). */
export const STATUS_META: Record<FleetStatus, { label: string }> = {
  moving: { label: 'Moving' },
  idling: { label: 'Idling' },
  parked: { label: 'Parked' },
  offline: { label: 'Offline' },
};

export const STATUS_ORDER: FleetStatus[] = ['moving', 'idling', 'parked', 'offline'];

export const getStatusMeta = (status: FleetStatus | string | null | undefined): { label: string } =>
  STATUS_META[status as FleetStatus] || STATUS_META.offline;

/** A vehicle's status as Live View shows it: the colour key, the words, where it came from. */
export interface LiveStatus {
  key: AssetStatusKey;
  label: string;
  /** true: the key is the LexiStar status board's; false: motion only. */
  lexistar: boolean;
  motion: FleetStatus;
  /** ST-00276: the display it was drawn from (colour, words, marks), when there is one. */
  display?: AssetDisplay | null;
}

/**
 * The status of a vehicle: its display (ST-00276) when there is one — the board row's, or the
 * organisation's rules over its motion —, else its LexiStar key, else its motion. `lexistarKey`
 * says whether the vehicle has a status board row.
 */
export function liveStatusOf(
  motion: FleetStatus,
  lexistarKey?: AssetStatusKey | null,
  display?: AssetDisplay | null
): LiveStatus {
  if (display) {
    const key = display.base.token as AssetStatusKey;
    return { key, label: display.base.label, lexistar: !!lexistarKey, motion, display };
  }
  if (lexistarKey) {
    return { key: lexistarKey, label: ASSET_STATUS[lexistarKey].label, lexistar: true, motion };
  }
  return {
    key: statusKeyOfMotion(motion),
    label: getStatusMeta(motion).label,
    lexistar: false,
    motion,
  };
}

/** Registration as a join key ("BMW320D-JL" → "BMW320DJL"). */
export const normaliseRegistration = (reg: string | null | undefined): string =>
  String(reg ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

interface ClassMeta {
  label: string;
  icon: ComponentType<{ size?: number }>;
  badge: string;
}

export const CLASS_META: Record<'truck' | 'loader', ClassMeta> = {
  truck: {
    label: 'Truck',
    icon: FaTruck,
    badge: 'bg-primary-50 text-primary-700 border-primary-100',
  },
  loader: {
    label: 'Loader',
    icon: FaTractor,
    badge: 'bg-amber-50 text-amber-700 border-amber-100',
  },
};

export const getClassMeta = (vehicleClass: string | null | undefined): ClassMeta =>
  CLASS_META[vehicleClass as 'truck' | 'loader'] || CLASS_META.loader;

/** Solid status dot in the vehicle's key colour; a moving vehicle gets a soft ping halo. */
export function StatusDot({ status, className = '' }: { status: LiveStatus; className?: string }) {
  const dot = ASSET_STATUS_CLASSES[status.key].dot;
  return (
    <span
      className={`relative inline-flex flex-shrink-0 ${className}`}
      data-status-key={status.key}
    >
      {status.motion === 'moving' && (
        <span
          className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${dot}`}
        />
      )}
      <span className={`relative inline-block w-2.5 h-2.5 rounded-full ${dot}`} />
    </span>
  );
}

/** Small labelled status pill in the vehicle's key colour. */
export function StatusPill({ status }: { status: LiveStatus }) {
  const c = ASSET_STATUS_CLASSES[status.key];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${c.pill}`}
      data-status-key={status.key}
      title={status.lexistar ? ASSET_STATUS[status.key].hint : undefined}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {status.label}
    </span>
  );
}

/** A vehicle's pill and, ST-00276, its overlay marks (the dashed outline is the marker's own). */
export function StatusWithMarks({ status }: { status: LiveStatus }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <StatusPill status={status} />
      <DisplayMarks overlays={status.display?.overlays} />
    </span>
  );
}

/** Truck / loader asset-class badge. */
export function ClassBadge({ vehicleClass }: { vehicleClass?: string | null }) {
  const meta = getClassMeta(vehicleClass);
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded border whitespace-nowrap ${meta.badge}`}
    >
      <Icon size={9} />
      {meta.label}
    </span>
  );
}

/**
 * Compact "3m ago" style relative age. Prefers the live `ts` (kept fresh by socket patches) and
 * falls back to the server-computed `ageSeconds`.
 */
export function compactAge(ts: string | null | undefined, ageSeconds?: number | null): string {
  let seconds = ageSeconds;
  if (ts) {
    const ms = Date.now() - new Date(ts).getTime();
    if (!Number.isNaN(ms)) seconds = Math.max(0, Math.round(ms / 1000));
  }
  if (seconds == null || Number.isNaN(seconds)) return '—';
  if (seconds < 45) return 'just now';
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
